import { defineComponent, h, ref, useAttrs } from 'vue'
import { VCounter, VTextField } from 'vuetify/components'
import {
  resolveBgColor,
  resolveDensity,
  resolveLegacyModelValue,
  resolveVariant,
  stripLegacyInputAttrs,
  withLegacyDirtyClass
} from './legacyInputProps'

VTextField.compatConfig = {
  ...(VTextField.compatConfig || {}),
  COMPONENT_V_MODEL: false,
  INSTANCE_ATTRS_CLASS_STYLE: 'suppress-warning'
}

VCounter.compatConfig = {
  ...(VCounter.compatConfig || {}),
  COMPONENT_FUNCTIONAL: 'suppress-warning'
}

// Vuetify's VCounter is a setup/render component with a stale Vue 2
// `functional` marker. Removing the marker avoids Vue compat treating it as an
// old render-function component and preserves the existing counter output.
delete VCounter.functional

export default defineComponent({
  name: 'VTextFieldCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,
  emits: ['update:modelValue', 'input', 'change'],

  setup(_, { emit, slots }) {
    const attrs = useAttrs()
    let lastExternalValue
    const localValue = ref()

    return () => {
      const normalizedAttrs = withLegacyDirtyClass(
        stripLegacyInputAttrs(attrs),
        attrs
      )
      const legacyInput = normalizedAttrs.onInput
      const legacyChange = normalizedAttrs.onChange
      const variant = resolveVariant(attrs)
      const density = resolveDensity(attrs)
      const bgColor = resolveBgColor(attrs)
      const externalValue = resolveLegacyModelValue(attrs)
      if (lastExternalValue !== externalValue) {
        lastExternalValue = externalValue
        localValue.value = externalValue
      } else if (localValue.value === undefined) {
        localValue.value = externalValue
      }
      const modelValue = localValue.value
      const resolveEventValue = (value) =>
        value?.target ? value.target.value : value
      const notifyInput = (value) => {
        const resolvedValue = resolveEventValue(value)
        if (resolvedValue === localValue.value) return
        localValue.value = resolvedValue
        emit('update:modelValue', resolvedValue)
        emit('input', resolvedValue)
        if (typeof legacyInput === 'function') legacyInput(resolvedValue)
      }
      const notifyChange = (value) => {
        const resolvedValue = resolveEventValue(value)
        emit('change', resolvedValue)
        if (typeof legacyChange === 'function') legacyChange(resolvedValue)
      }

      delete normalizedAttrs.onInput
      delete normalizedAttrs.onChange
      delete normalizedAttrs['onUpdate:modelValue']

      return h(
        VTextField,
        {
          ...normalizedAttrs,
          ...(variant ? { variant } : {}),
          ...(density ? { density } : {}),
          ...(bgColor ? { bgColor } : {}),
          modelValue,
          onInput: notifyInput,
          onChange: notifyChange,
          'onUpdate:modelValue': notifyInput
        },
        slots
      )
    }
  }
})
